/* House of Halima — predictive storefront search. Uses Shopify's locale-aware Ajax search endpoint. */
(() => {
  'use strict';
  const selectors = '.hoh-search-form, .hoh-search-large form';
  const formatter = new Intl.NumberFormat('en-GB', { style: 'currency', currency: 'GBP' });
  const make = (tag, className, text) => {
    const node = document.createElement(tag);
    if (className) node.className = className;
    if (text !== undefined) node.textContent = text;
    return node;
  };
  const safeUrl = (value) => {
    if (typeof value !== 'string' || !value) return null;
    try {
      const url = new URL(value, window.location.origin);
      return url.origin === window.location.origin && /^https?:$/.test(url.protocol) ? url.href : null;
    } catch (_) { return null; }
  };
  const money = (price) => {
    if (price == null || price === '') return '';
    const amount = Number(price);
    if (!Number.isFinite(amount)) return '';
    return formatter.format(amount / 100);
  };
  const picture = (product) => {
    const media = product.featured_image || product.image;
    if (typeof media === 'string') return media;
    if (media && typeof media === 'object') return media.url || media.src || '';
    return '';
  };
  const rootPath = String(window.Shopify?.routes?.root || '/');
  const searchRoot = rootPath.endsWith('/') ? rootPath : rootPath + '/';

  document.querySelectorAll(selectors).forEach((form, index) => {
    const input = form.querySelector('input[name="q"]');
    if (!input || form.dataset.hohPredictiveReady === 'true') return;
    form.dataset.hohPredictiveReady = 'true';
    input.setAttribute('autocomplete', 'off');
    input.setAttribute('role', 'combobox');
    input.setAttribute('aria-autocomplete', 'list');
    input.setAttribute('aria-haspopup', 'listbox');
    input.setAttribute('aria-expanded', 'false');

    const region = make('div', 'hoh-predictive-results');
    region.hidden = true;
    region.setAttribute('role', 'region');
    region.setAttribute('aria-label', 'Search suggestions');
    region.id = 'HohSuggestions-' + index;
    form.insertAdjacentElement('afterend', region);
    input.setAttribute('aria-controls', region.id);

    let timer = null;
    let controller = null;
    let revision = 0;
    let items = [];
    let active = -1;
    const resetActive = () => {
      active = -1;
      input.removeAttribute('aria-activedescendant');
      items.forEach((node) => node.classList.remove('is-active'));
    };
    const close = () => {
      region.hidden = true;
      input.setAttribute('aria-expanded', 'false');
      input.setAttribute('aria-busy', 'false');
      resetActive();
    };
    const show = () => {
      region.hidden = false;
      input.setAttribute('aria-expanded', 'true');
    };
    const option = (label, url, className) => {
      const link = make('a', className);
      link.href = url;
      link.setAttribute('role', 'option');
      link.setAttribute('aria-selected', 'false');
      link.id = region.id + '-option-' + items.length;
      items.push(link);
      return link;
    };
    const render = (data, term) => {
      region.replaceChildren();
      items = [];
      active = -1;
      const results = data?.resources?.results || {};
      const products = Array.isArray(results.products) ? results.products.slice(0, 6) : [];
      const collections = Array.isArray(results.collections) ? results.collections.slice(0, 3) : [];
      const queries = Array.isArray(results.queries) ? results.queries.slice(0, 3) : [];
      const addSection = (title) => region.appendChild(make('p', 'hoh-predictive-heading', title));

      if (products.length) {
        addSection('MATCHING PRODUCTS');
        const list = make('div', 'hoh-predictive-products');
        list.setAttribute('role', 'listbox');
        list.setAttribute('aria-label', 'Matching products');
        products.forEach((product) => {
          const url = safeUrl(product.url);
          if (!url) return;
          const link = option(product.title, url, 'hoh-predictive-product');
          const src = picture(product);
          const visual = make('span', 'hoh-predictive-image');
          if (src) {
            const img = make('img');
            img.src = src;
            img.alt = '';
            img.loading = 'lazy';
            img.width = 60;
            img.height = 78;
            visual.appendChild(img);
          }
          const content = make('span', 'hoh-predictive-copy');
          content.appendChild(make('span', 'hoh-predictive-title', product.title || 'View product'));
          const amount = money(product.price);
          if (amount) content.appendChild(make('span', 'hoh-predictive-price', amount));
          if (product.available === false) {
            link.classList.add('is-unavailable');
            content.appendChild(make('span', 'hoh-predictive-stock', 'Sold out'));
          }
          link.append(visual, content);
          list.appendChild(link);
        });
        region.appendChild(list);
      }
      const textMatches = [...queries, ...collections];
      if (textMatches.length) {
        addSection('OTHER SUGGESTIONS');
        const list = make('div', 'hoh-predictive-others');
        list.setAttribute('role', 'listbox');
        list.setAttribute('aria-label', 'Other suggestions');
        textMatches.forEach((item) => {
          const title = item.styled_text || item.text || item.title;
          const cleanTitle = String(title || '').replace(/<[^>]*>/g, '');
          const url = safeUrl(item.url);
          if (!cleanTitle || !url) return;
          const link = option(cleanTitle, url, 'hoh-predictive-other');
          link.append(make('span', 'hoh-predictive-other-icon', '⌕'), make('span', '', cleanTitle));
          list.appendChild(link);
        });
        region.appendChild(list);
      }
      if (!items.length) {
        region.appendChild(make('p', 'hoh-predictive-empty', 'No matching suggestions. Try another word or search the full catalogue.'));
      }
      const all = make('a', 'hoh-predictive-all', 'VIEW ALL RESULTS FOR “' + term + '” →');
      const allUrl = new URL(searchRoot + 'search', window.location.origin);
      allUrl.searchParams.set('q', term);
      allUrl.searchParams.set('type', 'product');
      all.href = allUrl.toString();
      region.appendChild(all);
      input.removeAttribute('aria-activedescendant');
      input.setAttribute('aria-busy', 'false');
      show();
    };
    const load = async (term, id) => {
      controller?.abort();
      controller = new AbortController();
      const signal = controller.signal;
      try {
        const endpoint = new URL(searchRoot + 'search/suggest.json', window.location.origin);
        endpoint.searchParams.set('q', term);
        endpoint.searchParams.set('resources[type]', 'product,collection,query');
        endpoint.searchParams.set('resources[limit]', '6');
        endpoint.searchParams.set('resources[options][unavailable_products]', 'last');
        const response = await fetch(endpoint.toString(), {
          signal,
          headers: { Accept: 'application/json' },
          credentials: 'same-origin'
        });
        if (!response.ok) throw new Error('Suggestions unavailable');
        const result = await response.json();
        if (id !== revision || document.activeElement !== input) return;
        render(result, term);
      } catch (error) {
        if (signal.aborted || id !== revision) return;
        input.setAttribute('aria-busy', 'false');
        region.replaceChildren(make('p', 'hoh-predictive-empty', 'Suggestions are unavailable. Press Enter to search.'));
        show();
      }
    };
    input.addEventListener('input', () => {
      window.clearTimeout(timer);
      controller?.abort();
      revision++;
      close();
      region.replaceChildren();
      const term = input.value.trim();
      if (term.length < 2) { close(); return; }
      input.setAttribute('aria-busy', 'true');
      timer = window.setTimeout(() => load(term, revision), 260);
    });
    input.addEventListener('focus', () => {
      if (input.value.trim().length >= 2 && region.childNodes.length > 0) show();
      else if (input.value.trim().length >= 2) input.dispatchEvent(new Event('input'));
    });
    input.addEventListener('keydown', (event) => {
      if (event.key === 'Escape') {
        if (!region.hidden) { event.preventDefault(); event.stopPropagation(); close(); }
        return;
      }
      if (region.hidden || !items.length) return;
      if (event.key === 'ArrowDown' || event.key === 'ArrowUp') {
        event.preventDefault();
        items.forEach((node) => {
          node.classList.remove('is-active');
          node.setAttribute('aria-selected', 'false');
        });
        active = event.key === 'ArrowDown' ? (active + 1) % items.length : (active + items.length - 1) % items.length;
        items[active].classList.add('is-active');
        items[active].setAttribute('aria-selected', 'true');
        input.setAttribute('aria-activedescendant', items[active].id);
        items[active].scrollIntoView({block: 'nearest'});
      } else if (event.key === 'Enter' && active >= 0 && items[active]) {
        event.preventDefault();
        window.location.assign(items[active].href);
      }
    });
    document.addEventListener('pointerdown', (event) => {
      if (event.target !== input && !region.contains(event.target) && !form.contains(event.target)) close();
    });
    form.addEventListener('submit', () => {
      window.clearTimeout(timer);
      controller?.abort();
      close();
    });
    const closeButton = form.querySelector('[data-hoh-search-close]');
    closeButton?.addEventListener('click', close);
  });
})();